import React, { useState, useEffect, useCallback } from 'react';
import ImportMethodSelector from '../ImportMethodSelector';
import CustomerSelector from '../CustomerSelector';
import AddressSelector from '../AddressSelector';
import ProfileSelector from '../ProfileSelector';
import CSVImporter from '../CSVImporter';
import csvImportApiClient from '../../utilities/csvImportApiClient';
import Styles from './styles.module.css';

const CSVOrderImport = () => {
    // Import method state
    const [importMethod, setImportMethod] = useState(null); // 'profile' or 'manual'
    
    // Existing states
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [salesChannels, setSalesChannels] = useState([]);
    const [shippingMethods, setShippingMethods] = useState([]);
    const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

    const handleCustomerSearch = useCallback(async (searchTerm) => {
        setIsLoadingCustomers(true);
        try {
            const response = await csvImportApiClient.getCustomers(searchTerm);
            // Component might be unmounted by now, check before setting state
            setCustomers(response.success ? (response.data || []) : []);
            if (!response.success) {
                console.error('Customer search failed:', response.error);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomers([]);
        } finally {
            setIsLoadingCustomers(false);
        }
    }, []);

    const handleCustomerSelect = async (customer) => {
        console.log('[Customer Select] Selected customer:', customer);
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setAddresses([]);
        
        // Fetch full customer details with group information
        if (customer?.id) {
            console.log('[Customer Select] Loading full customer details for:', customer.id);
            setIsLoadingAddresses(true);
            
            try {
                // Load customer details with group information
                const customerDetails = await csvImportApiClient.getCustomerDetails(customer.id);
                console.log('[Customer Select] Fetched customer details:', customerDetails);
                
                // Set the customer with full details
                setSelectedCustomer(customerDetails || customer);
                
                // Load addresses
                const customerAddresses = await csvImportApiClient.getCustomerAddresses(customer.id);
                console.log('[Customer Select] Fetched addresses:', customerAddresses);
                
                if (Array.isArray(customerAddresses)) {
                    setAddresses(customerAddresses);
                    // Auto-select default addresses if available
                    const defaultBilling = customerAddresses.find(addr => addr.id === customer.defaultBillingAddressId);
                    const defaultShipping = customerAddresses.find(addr => addr.id === customer.defaultShippingAddressId);
                    console.log('[Customer Select] Default billing:', defaultBilling);
                    console.log('[Customer Select] Default shipping:', defaultShipping);
                    if (defaultBilling) setSelectedBillingAddress(defaultBilling);
                    if (defaultShipping) setSelectedShippingAddress(defaultShipping);
                } else {
                    console.error('[Customer Select] Invalid address format received:', customerAddresses);
                    setAddresses([]);
                }
            } catch (error) {
                console.error('Error fetching customer details:', error);
                // Fallback to basic customer info
                setSelectedCustomer(customer);
                setAddresses([]);
            } finally {
                setIsLoadingAddresses(false);
            }
        } else {
            setSelectedCustomer(customer);
        }
    };

    const handleBillingAddressSelect = (address) => {
        setSelectedBillingAddress(address);
    };

    const handleShippingAddressSelect = (address) => {
        setSelectedShippingAddress(address);
    };

    const handleProfileSelect = (profile) => {
        setSelectedProfile(profile);
    };

    const loadCustomerFromId = async (customerId) => {
        console.log('[Profile Import] Loading customer by ID:', customerId);
        try {
            const customerDetails = await csvImportApiClient.getCustomerDetails(customerId);
            console.log('[Profile Import] Loaded customer details:', customerDetails);
            
            if (customerDetails) {
                setSelectedCustomer(customerDetails);
                
                // Load addresses for this customer
                const customerAddresses = await csvImportApiClient.getCustomerAddresses(customerId);
                console.log('[Profile Import] Loaded customer addresses:', customerAddresses);
                
                if (Array.isArray(customerAddresses)) {
                    setAddresses(customerAddresses);
                    // Auto-select default addresses if available
                    const defaultBilling = customerAddresses.find(addr => addr.id === customerDetails.defaultBillingAddressId);
                    const defaultShipping = customerAddresses.find(addr => addr.id === customerDetails.defaultShippingAddressId);
                    if (defaultBilling) setSelectedBillingAddress(defaultBilling);
                    if (defaultShipping) setSelectedShippingAddress(defaultShipping);
                }
            }
        } catch (error) {
            console.error('[Profile Import] Error loading customer by ID:', error);
        }
    };

    // New handler functions for import method selection
    const handleProfileImport = (profile) => {
        console.log('[Profile Import] === FULL PROFILE DEBUG ===');
        console.log('[Profile Import] Complete profile object:', JSON.stringify(profile, null, 2));
        console.log('[Profile Import] Profile keys:', Object.keys(profile));
        console.log('[Profile Import] Profile has customer:', !!profile.customer);
        console.log('[Profile Import] Profile has billingAddress:', !!profile.billingAddress);
        console.log('[Profile Import] Profile has shippingAddress:', !!profile.shippingAddress);
        
        setImportMethod('profile');
        setSelectedProfile(profile);
        
        // Auto-load customer and addresses from profile (if available)
        if (profile.customer) {
            console.log('[Profile Import] Loading customer from profile:', profile.customer);
            setSelectedCustomer(profile.customer);
            
            // Create addresses array from profile data
            const profileAddresses = [];
            if (profile.billingAddress) {
                profileAddresses.push(profile.billingAddress);
            }
            if (profile.shippingAddress && profile.shippingAddress.id !== profile.billingAddress?.id) {
                profileAddresses.push(profile.shippingAddress);
            }
            
            setAddresses(profileAddresses);
            
            if (profile.billingAddress) {
                console.log('[Profile Import] Loading billing address from profile:', profile.billingAddress);
                setSelectedBillingAddress(profile.billingAddress);
            }
            if (profile.shippingAddress) {
                console.log('[Profile Import] Loading shipping address from profile:', profile.shippingAddress);
                setSelectedShippingAddress(profile.shippingAddress);
            }
        } else if (profile.defaultValues?.customerId) {
            console.log('[Profile Import] Loading customer from defaultValues.customerId:', profile.defaultValues.customerId);
            // Try to load customer from customerId in defaultValues
            loadCustomerFromId(profile.defaultValues.customerId);
        } else {
            console.log('[Profile Import] No customer in profile - will need manual selection');
            // Profile without predefined customer - will need manual selection
            setSelectedCustomer(null);
            setSelectedBillingAddress(null);
            setSelectedShippingAddress(null);
            setAddresses([]);
        }
    };

    const handleManualImport = () => {
        console.log('[Manual Import] Starting manual import workflow');
        setImportMethod('manual');
        // Reset all selections for manual workflow
        setSelectedCustomer(null);
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setSelectedProfile(null);
        setAddresses([]);
    };

    const handleBackToMethodSelection = () => {
        setImportMethod(null);
        setSelectedCustomer(null);
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setSelectedProfile(null);
        setAddresses([]);
        setCustomers([]);
    };

    const loadProfiles = async () => {
        setIsLoadingProfiles(true);
        try {
            const response = await csvImportApiClient.getProfiles();
            console.log('[CSVOrderImport] Profile response:', response);
            if (response.success) {
                console.log('[CSVOrderImport] Loaded profiles:', response.data);
                console.log('[CSVOrderImport] First profile structure:', response.data[0]);
                setProfiles(response.data || []);
            } else {
                console.error('Profile loading failed:', response.error);
                setProfiles([]);
            }
        } catch (error) {
            console.error('Error loading profiles:', error);
            setProfiles([]);
        } finally {
            setIsLoadingProfiles(false);
        }
    };

    const loadSalesChannels = async () => {
        try {
            const response = await csvImportApiClient.getSalesChannels();
            if (response.success) {
                setSalesChannels(response.data || []);
            } else {
                console.error('Sales channels loading failed:', response.error);
                setSalesChannels([]);
            }
        } catch (error) {
            console.error('Error loading sales channels:', error);
            setSalesChannels([]);
        }
    };

    const loadShippingMethods = async () => {
        try {
            const response = await csvImportApiClient.getShippingMethods();
            if (response.success) {
                setShippingMethods(response.data || []);
            } else {
                console.error('Shipping methods loading failed:', response.error);
                setShippingMethods([]);
            }
        } catch (error) {
            console.error('Error loading shipping methods:', error);
            setShippingMethods([]);
        }
    };

    const getImportTypeDisplay = (profile) => {
        if (!profile) return 'Unbekannt';
        
        const importType = profile.importType || 'single-customer-multiple-orders';
        
        switch (importType) {
            case 'single-customer-multiple-orders':
                return 'Ein Kunde, mehrere Bestellungen';
            case 'single-customer-single-order-with-prices':
                return 'Ein Kunde, eine Bestellung (mit Preisen)';
            case 'single-customer-single-order-without-prices':
                return 'Ein Kunde, eine Bestellung (ohne Preise)';
            case 'multiple-customers-multiple-orders':
                return 'Mehrere Kunden, mehrere Bestellungen';
            default:
                return 'Benutzerdefiniert';
        }
    };

    useEffect(() => {
        loadProfiles();
        loadSalesChannels();
        loadShippingMethods();
        // Don't automatically search for customers on load to avoid loops
        // handleCustomerSearch('');
    }, []);

    return (
        <div className={Styles.container}>
            <div className={Styles.header}>
                <h1>CSV Order Import</h1>
                <p>Importieren Sie Bestellungen aus CSV-Dateien direkt in Ihr Shopware System</p>
            </div>

            <div className={Styles.workflowSection}>
                {/* Step 1: Import Method Selection */}
                {!importMethod && (
                    <div className={Styles.step}>
                        <div className={Styles.stepNumber}>1</div>
                        <ImportMethodSelector
                            profiles={profiles}
                            onProfileImport={handleProfileImport}
                            onManualImport={handleManualImport}
                            isLoadingProfiles={isLoadingProfiles}
                        />
                    </div>
                )}

                {/* Profile-based Import Workflow */}
                {importMethod === 'profile' && (
                    <>
                        <div className={Styles.step}>
                            <div className={Styles.stepNumber}>1</div>
                            <div className={Styles.profileImportSummary}>
                                <h3>Profile-basierter Import</h3>
                                <div className={Styles.summaryContent}>
                                    <div className={Styles.summaryRow}>
                                        <strong>Profil:</strong> {selectedProfile?.name}
                                    </div>
                                    <div className={Styles.summaryRow}>
                                        <strong>Import-Typ:</strong> {getImportTypeDisplay(selectedProfile)}
                                    </div>
                                    <div className={Styles.summaryRow}>
                                        <strong>Preisbehandlung:</strong> {selectedProfile?.defaultValues?.withPrices ? 'Mit Preisen' : 'Ohne Preise'}
                                    </div>
                                    <div className={Styles.summaryRow}>
                                        <strong>Verkaufskanal:</strong> {
                                            selectedProfile?.defaultValues?.salesChannelId 
                                                ? (salesChannels.find(sc => sc.id === selectedProfile.defaultValues.salesChannelId)?.name || 'Unbekannt')
                                                : 'Nicht konfiguriert'
                                        }
                                    </div>
                                    <div className={Styles.summaryRow}>
                                        <strong>Versandart:</strong> {
                                            selectedProfile?.defaultValues?.shippingMethodId 
                                                ? (shippingMethods.find(sm => sm.id === selectedProfile.defaultValues.shippingMethodId)?.name || 'Unbekannt')
                                                : 'Nicht konfiguriert'
                                        }
                                    </div>
                                    <div className={Styles.summaryRow}>
                                        <strong>Kunde:</strong> {
                                            selectedCustomer 
                                                ? (selectedCustomer.company || `${selectedCustomer.firstName} ${selectedCustomer.lastName}`)
                                                : 'Noch nicht ausgewählt'
                                        }
                                    </div>
                                    {selectedCustomer && (
                                        <>
                                            <div className={Styles.summaryRow}>
                                                <strong>Kundennummer:</strong> {selectedCustomer.customerNumber || 'Nicht verfügbar'}
                                            </div>
                                            <div className={Styles.summaryRow}>
                                                <strong>E-Mail:</strong> {selectedCustomer.email || 'Nicht verfügbar'}
                                            </div>
                                            <div className={Styles.summaryRow}>
                                                <strong>Kundengruppe:</strong> {
                                                    selectedCustomer.group?.name || 
                                                    selectedCustomer.group?.translated?.name ||
                                                    (selectedCustomer.group ? `ID: ${selectedCustomer.group.id}` : '') ||
                                                    (selectedCustomer.customerGroupId ? `Group ID: ${selectedCustomer.customerGroupId}` : '') ||
                                                    'Nicht verfügbar'
                                                }
                                            </div>
                                            <div className={Styles.summaryRow}>
                                                <strong>Kunden-Verkaufskanal:</strong> {
                                                    selectedCustomer.salesChannelId 
                                                        ? (salesChannels.find(sc => sc.id === selectedCustomer.salesChannelId)?.name || 
                                                           `ID: ${selectedCustomer.salesChannelId}`)
                                                        : 'Nicht verfügbar'
                                                }
                                            </div>
                                        </>
                                    )}
                                    {selectedProfile?.description && (
                                        <div className={Styles.summaryRow}>
                                            <strong>Beschreibung:</strong> {selectedProfile.description}
                                        </div>
                                    )}
                                </div>
                                <button 
                                    className={Styles.backButton}
                                    onClick={handleBackToMethodSelection}
                                >
                                    ← Zurück zur Methodenauswahl
                                </button>
                            </div>
                        </div>

                        {/* For profile-based import, decide workflow based on import type */}
                        {selectedProfile?.importType === 'multiple-customers-multiple-orders' ? (
                            // Multi-customer import: Go directly to CSV upload (customer data comes from CSV)
                            <div className={Styles.step}>
                                <div className={Styles.stepNumber}>2</div>
                                <div className={Styles.importSection}>
                                    <CSVImporter
                                        selectedCustomer={null}
                                        selectedBillingAddress={null}
                                        selectedShippingAddress={null}
                                        selectedProfile={selectedProfile}
                                        salesChannels={salesChannels}
                                        shippingMethods={shippingMethods}
                                    />
                                </div>
                            </div>
                        ) : (
                            // Single customer import: Need customer selection first
                            <>
                                {/* If profile has no predefined customer, show customer selection */}
                                {!selectedCustomer && (
                                    <div className={Styles.step}>
                                        <div className={Styles.stepNumber}>2</div>
                                        <CustomerSelector
                                            customers={customers}
                                            selectedCustomer={selectedCustomer}
                                            onCustomerSelect={handleCustomerSelect}
                                            onCustomerSearch={handleCustomerSearch}
                                            isLoading={isLoadingCustomers}
                                        />
                                    </div>
                                )}

                                {/* Address selection for profile-based import - only if customer but missing addresses */}
                                {selectedCustomer && !selectedBillingAddress && (
                                    <div className={Styles.step}>
                                        <div className={Styles.stepNumber}>3</div>
                                        <AddressSelector
                                            addresses={addresses}
                                            selectedBillingAddress={selectedBillingAddress}
                                            selectedShippingAddress={selectedShippingAddress}
                                            onBillingAddressSelect={handleBillingAddressSelect}
                                            onShippingAddressSelect={handleShippingAddressSelect}
                                            isLoading={isLoadingAddresses}
                                            customer={selectedCustomer}
                                        />
                                    </div>
                                )}

                                {/* CSV Import section - show when profile selected and customer/addresses ready */}
                                {selectedProfile && selectedCustomer && selectedBillingAddress && selectedShippingAddress && (
                                    <div className={Styles.step}>
                                        <div className={Styles.stepNumber}>4</div>
                                        <div className={Styles.importSection}>
                                            <CSVImporter
                                                selectedCustomer={selectedCustomer}
                                                selectedBillingAddress={selectedBillingAddress}
                                                selectedShippingAddress={selectedShippingAddress}
                                                selectedProfile={selectedProfile}
                                                salesChannels={salesChannels}
                                                shippingMethods={shippingMethods}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* Manual Import Workflow */}
                {importMethod === 'manual' && (
                    <>
                        <div className={Styles.step}>
                            <div className={Styles.stepNumber}>1</div>
                            <div className={Styles.manualImportHeader}>
                                <h3>Individueller Import</h3>
                                <button 
                                    className={Styles.backButton}
                                    onClick={handleBackToMethodSelection}
                                >
                                    ← Zurück zur Methodenauswahl
                                </button>
                            </div>
                        </div>

                        <div className={Styles.step}>
                            <div className={Styles.stepNumber}>2</div>
                            <CustomerSelector
                                customers={customers}
                                selectedCustomer={selectedCustomer}
                                onCustomerSelect={handleCustomerSelect}
                                onCustomerSearch={handleCustomerSearch}
                                isLoading={isLoadingCustomers}
                            />
                        </div>

                        {selectedCustomer && (
                            <div className={Styles.step}>
                                <div className={Styles.stepNumber}>3</div>
                                <AddressSelector
                                    addresses={addresses}
                                    selectedBillingAddress={selectedBillingAddress}
                                    selectedShippingAddress={selectedShippingAddress}
                                    onBillingAddressSelect={handleBillingAddressSelect}
                                    onShippingAddressSelect={handleShippingAddressSelect}
                                    isLoading={isLoadingAddresses}
                                    customer={selectedCustomer}
                                />
                            </div>
                        )}

                        {selectedCustomer && selectedBillingAddress && selectedShippingAddress && (
                            <div className={Styles.step}>
                                <div className={Styles.stepNumber}>4</div>
                                <ProfileSelector
                                    profiles={profiles}
                                    selectedProfile={selectedProfile}
                                    onProfileSelect={handleProfileSelect}
                                    isLoading={isLoadingProfiles}
                                    getImportTypeDisplay={getImportTypeDisplay}
                                />
                            </div>
                        )}

                        {selectedCustomer && selectedBillingAddress && selectedShippingAddress && selectedProfile && (
                            <div className={Styles.step}>
                                <div className={Styles.stepNumber}>5</div>
                                <div className={Styles.importSection}>
                                    <CSVImporter
                                        selectedCustomer={selectedCustomer}
                                        selectedBillingAddress={selectedBillingAddress}
                                        selectedShippingAddress={selectedShippingAddress}
                                        selectedProfile={selectedProfile}
                                        salesChannels={salesChannels}
                                        shippingMethods={shippingMethods}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CSVOrderImport;